import { useMutation } from '@apollo/client';
import React, { useState } from 'react';
import { match } from 'ts-pattern';

import { useCompleteFragment } from '@eluve/apollo-client';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Stack,
  Switch,
  Text,
  useToast,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';

export const tenantPendingApprovalFragment = graphql(`
  fragment TenantPendingApproval on AdminTenants @_unmask {
    __typename
    id
    name
    isPendingApproval
  }
`);

const setTenantPendingApprovalMutation = graphql(
  `
    mutation setTenantPendingApproval(
      $tenantId: uuid!
      $isPendingApproval: Boolean!
    ) {
      updateAdminTenants(
        where: { id: { _eq: $tenantId } }
        _set: { isPendingApproval: $isPendingApproval }
      ) {
        returning {
          ...TenantPendingApproval
        }
      }
    }
  `,
  [tenantPendingApprovalFragment],
);

export interface TenantApprovalToggleProps {
  tenantId: string;
}

export const TenantApprovalToggle: React.FC<TenantApprovalToggleProps> = ({
  tenantId,
}) => {
  const [nextPendingState, setNextPendingState] = useState<boolean | undefined>(
    undefined,
  );
  const toast = useToast();

  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState(false);
  const [confirmationInput, setConfirmationInput] = useState('');

  const tenant = useCompleteFragment({
    fragment: tenantPendingApprovalFragment,
    key: {
      id: tenantId,
    },
  });

  const [updateTenantApproval] = useMutation(setTenantPendingApprovalMutation, {
    onError: () => toast.error('Failed to update organization approval status'),
    optimisticResponse: (data) => ({
      updateAdminTenants: {
        returning: [
          {
            __typename: 'AdminTenants' as const,
            id: tenantId,
            name: tenant.name,
            isPendingApproval: data.isPendingApproval,
          },
        ],
      },
    }),
  });

  const handleSave = () => {
    if (nextPendingState === undefined) {
      return;
    }

    updateTenantApproval({
      variables: {
        tenantId,
        isPendingApproval: nextPendingState,
      },
    });

    setIsConfirmationDialogOpen(false);
    setNextPendingState(undefined);
    setConfirmationInput('');
  };

  const cancelApprovalChange = () => {
    setIsConfirmationDialogOpen(false);
    setNextPendingState(undefined);
    setConfirmationInput('');
  };

  const isConfirmationValid =
    tenant.name?.toLowerCase().startsWith(confirmationInput.toLowerCase()) &&
    confirmationInput.length >= 5;

  return (
    <>
      <Dialog
        open={isConfirmationDialogOpen}
        onOpenChange={(open) => {
          if (open === false) {
            cancelApprovalChange();
          }
        }}
      >
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>
              {match(nextPendingState)
                .with(true, () => 'Remove Organization Approval')
                .otherwise(() => 'Approve Organization')}
            </DialogTitle>
            <DialogDescription>
              {match(nextPendingState)
                .with(
                  true,
                  () =>
                    'Users will no longer be able to access this organization.',
                )
                .otherwise(
                  () => 'This organization will be enabled for its users.',
                )}
            </DialogDescription>
          </DialogHeader>
          <Stack gap={4}>
            <Text size="md">
              Please type at least the first 5 characters of the organization
              name (
              <span className="font-bold text-text-negative">
                {tenant.name}
              </span>
              ) to confirm:
            </Text>
            <Input
              value={confirmationInput}
              onChange={(e) => setConfirmationInput(e.target.value)}
              className="mt-2"
            />
          </Stack>
          <DialogFooter>
            <Button variant="outline" onClick={cancelApprovalChange}>
              Cancel
            </Button>

            <Button onClick={handleSave} disabled={!isConfirmationValid}>
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Switch
        onCheckedChange={(checked) => {
          setNextPendingState(!checked);
          setIsConfirmationDialogOpen(true);
        }}
        checked={!tenant.isPendingApproval}
        aria-label="Is Approved"
      />
    </>
  );
};
